import { useState } from "react";
import { getAllStoreOptions, getPreferredStoreId } from "@/lib/preferredStore";
import {
  hasActiveAffiliateProgram,
  useAffiliateConfig,
  withAffiliateTag,
} from "@/lib/affiliateConfig";
import { useTranslations } from "@/contexts/I18nContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";

interface SendToStoreSheetProps {
  query: string;
  preferred?: string;
  onClose: () => void;
}

const ADULT_MEMBER_TYPES = new Set(["adult", "elderly_parent"]);

/**
 * Modal "send to which store?" picker. The user always picks the
 * retailer rather than being auto-sent to a single one. The user's
 * `preferred_store` (if set) floats to the top with a "Preferred" tag.
 *
 * Affiliate behavior: outbound URLs route through `withAffiliateTag`,
 * which appends the per-store attribution param when an active program
 * is configured in `config/affiliate_links`. Required disclosure
 * renders inside the sheet whenever any program is active. Whole sheet
 * is gated to adult-or-elderly_parent members per Amazon Associates
 * Operating Agreement and family-app rules.
 *
 * See [[activate-amazon-associates-tag-zoeyorganizer-20-ac]].
 */
export function SendToStoreSheet({
  query,
  preferred,
  onClose,
}: SendToStoreSheetProps) {
  const t = useTranslations();
  const { profile } = useHouseholdContext();
  const config = useAffiliateConfig();
  const options = getAllStoreOptions(preferred);
  const preferredId = getPreferredStoreId(preferred);
  const trimmedQuery = query.trim();
  const [showLongForm, setShowLongForm] = useState(false);

  const memberType = profile?.memberType ?? "";
  const isAdult = ADULT_MEMBER_TYPES.has(memberType);

  // Affiliate CTAs must not be exposed to non-adults. Render nothing —
  // the trigger button on the parent page should also be hidden, this
  // is the defense-in-depth fallback.
  if (!isAdult) return null;

  const showDisclosure = hasActiveAffiliateProgram(config);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-white/10">
          <h3 className="font-semibold text-sm text-center">
            {t("sendToStore.title")}
          </h3>
          {trimmedQuery && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1 truncate">
              &ldquo;{trimmedQuery}&rdquo;
            </p>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
          {options.map((opt) => {
            const isPreferred = preferredId !== null && opt.id === preferredId;
            const baseUrl = opt.search(trimmedQuery);
            const taggedUrl = withAffiliateTag(baseUrl, opt.id, config);
            return (
              <a
                key={opt.id}
                href={taggedUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="flex items-center justify-between w-full px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <span className={isPreferred ? "font-semibold" : ""}>
                  {opt.label}
                </span>
                {isPreferred && (
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">
                    {t("sendToStore.preferredTag")}
                  </span>
                )}
              </a>
            );
          })}
        </div>

        {showDisclosure && (
          <div className="px-4 py-2 border-t border-gray-100 dark:border-white/10 text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <span>Some links may earn Zoey a commission.</span>
            <button
              type="button"
              onClick={() => setShowLongForm(true)}
              className="underline hover:text-gray-700 dark:hover:text-gray-200"
            >
              Learn more
            </button>
          </div>
        )}

        <div className="p-2 border-t border-gray-100 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>

      {showLongForm && (
        <AffiliateDisclosureLongForm onClose={() => setShowLongForm(false)} />
      )}
    </div>
  );
}

/** Long-form Amazon Associates disclosure modal. Linked to from the
 *  inline caption in `SendToStoreSheet`. Required by §10 of the
 *  Operating Agreement. */
function AffiliateDisclosureLongForm({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5">
          <h2 className="text-lg font-bold mb-3">Affiliate links in Zoey</h2>
          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <p>
              Some links in Zoey go to retailer storefronts (currently
              Amazon) using Zoey&apos;s affiliate program identifiers. When
              you tap one of these links and complete a purchase, the
              retailer may pay Zoey a commission at no additional cost
              to you.
            </p>
            <p>
              <span className="font-semibold">Amazon Associates.</span>{" "}
              Zoey is a participant in the Amazon Services LLC Associates
              Program, an affiliate advertising program designed to
              provide a means for sites to earn advertising fees by
              advertising and linking to Amazon.com. As an Amazon
              Associate, Zoey earns from qualifying purchases.
            </p>
            <p>
              <span className="font-semibold">What we share.</span> Zoey
              does not share any personal information with retailers as
              part of this program — we only attach a static identifier
              to the outbound URL so the retailer knows the visit came
              from Zoey.
            </p>
            <p>
              <span className="font-semibold">Visibility.</span>{" "}
              Affiliate-link buttons are only shown to adult members of
              your household. Children and other member types do not see
              them.
            </p>
          </div>
        </div>
        <div className="p-3 border-t border-gray-100 dark:border-white/10 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-white/10 dark:hover:bg-white/15 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
